import React, { useRef } from 'react';
import useCanvasInitialization from '../hooks/useCanvasInitialization';
import useCanvasViewport from '../hooks/useCanvasViewport';
import useClassAttention from '../hooks/useClassAttention';
import useLabelingStore from '../store/useDetectionStore';
import useDetectionRightStore from '../store/componentStores/useDetectionRightStore';
import Button from '../../../../../components/Atoms/Button.jsx';

/**
 * The canvas stage: a viewport that always fills the panel, with the image
 * fitted into it, plus non-interactive overlays (file chip, status bar, hints).
 */
const DetectionMiddle = () => {
  const {
    setCanvas,
    getCurrentImage,
    isDrawingPolygon,
    zoomLevel,
    images,
    currentImageIndex,
    mode,
    classes,
  } = useLabelingStore();

  const { setClassInfo, setIsModalOpen } = useDetectionRightStore();
  const isNudging = useClassAttention();
  const viewportRef = useRef(null);
  const canvasRef = useCanvasInitialization(setCanvas);
  const canvas = useLabelingStore((state) => state.canvas);
  const { cursor, isPanning, isSpaceHeld } = useCanvasViewport(canvas, viewportRef);

  const image = getCurrentImage();
  const hasImage = !!image?.fileName;
  const boxes = image?.annotations?.length ?? 0;
  const polygons = image?.polygons?.length ?? 0;

  const cursorMode = isPanning ? 'panning' : isSpaceHeld ? 'grab' : mode;
  const needsClass = (classes?.length ?? 0) === 0;

  const openAddClass = () => {
    setClassInfo(null);
    setIsModalOpen(true);
  };

  return (
    <div className="lf-canvas-stage">
      <div
        ref={viewportRef}
        className="lf-canvas-viewport"
        data-cursor={cursorMode}
        data-empty={hasImage ? undefined : 'true'}
      >
        <canvas ref={canvasRef} />
      </div>

      {hasImage ? (
        <>
          <div className="lf-canvas-chip lf-canvas-chip--file" title={image.fileName}>
            <i className="fa fa-image" aria-hidden="true"></i>
            <span className="lf-canvas-chip__name">{image.fileName}</span>
            <span className="lf-canvas-chip__index">
              {currentImageIndex + 1} / {images.length}
            </span>
          </div>

          <div className="lf-canvas-statusbar">
            <span className="lf-canvas-stat">
              <i className="fa fa-expand" aria-hidden="true"></i>
              {image.originalWidth || '?'} × {image.originalHeight || '?'}
            </span>
            <span className="lf-canvas-stat">
              <i className="fa fa-search" aria-hidden="true"></i>
              {Math.round(zoomLevel * 100)}%
            </span>
            <span className="lf-canvas-stat lf-canvas-stat--coords">
              <i className="fa fa-crosshairs" aria-hidden="true"></i>
              {cursor ? `${cursor.x}, ${cursor.y}` : '—, —'}
            </span>
            <span className="lf-canvas-stat">
              <i className="fa fa-vector-square" aria-hidden="true"></i>
              {boxes} {boxes === 1 ? 'box' : 'boxes'}
              {polygons > 0 ? ` · ${polygons} poly` : ''}
            </span>
            <span className="lf-canvas-stat lf-canvas-stat--hint">
              Scroll to zoom · Space or middle-drag to pan
            </span>
          </div>

          {needsClass ? (
            <div className={`lf-canvas-callout ${isNudging ? 'lf-canvas-callout--nudge' : ''}`}>
              <i className="fa fa-shapes" aria-hidden="true"></i>
              <span className="lf-canvas-callout__text">
                <strong>No label classes yet</strong>
                Create one to start labeling.
              </span>
              <Button className="lf-canvas-callout__action" onClick={openAddClass}>
                <i className="fa fa-plus" aria-hidden="true"></i> Add class
              </Button>
            </div>
          ) : (
            isDrawingPolygon && (
              <div className="lf-canvas-hint">
                Click to add points · click the first point or press Enter to close · Esc to cancel
              </div>
            )
          )}
        </>
      ) : (
        <div className="lf-canvas-empty">
          <div className="initial-overlay">
            <i className="feather icon-image" aria-hidden="true"></i>
            <p>Select an image to start labeling</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetectionMiddle;
